import { Button, Flex, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { useUI } from "../../../../../contexts/ui";
import { canManageKnowledgeDatabase } from "../../../../../utils/auth";
import { errorMessage } from "../../../../../utils/http";
import {
  useBuildKnowledgeGraphMutation,
  useKnowledgeGraphStatusQuery,
} from "../../../../../utils/queries/knowledge";

/**
 * "Build knowledge graph" and when the graph was last built.
 *
 * The build runs in warhol's per-enterprise refresh workflow, the same one a
 * reviewer's approve/reject and a stored memory trigger; the time shown is the
 * one warhol records when a build finishes.
 */
export function KnowledgeGraphBuildControl() {
  const { t, i18n } = useTranslation("ui");
  const { notify } = useUI();
  const canManage = canManageKnowledgeDatabase();
  const status = useKnowledgeGraphStatusQuery();
  const build = useBuildKnowledgeGraphMutation();

  const building = Boolean(status.data?.building) || build.isPending;
  const builtAt = status.data?.builtAt ?? null;
  function describeBuild(): string {
    if (building) {
      return t("settings.knowledgeDatabaseHeader.buildingKnowledgeGraph");
    }
    if (builtAt) {
      return t("settings.knowledgeDatabaseHeader.lastBuilt", {
        when: new Intl.DateTimeFormat(i18n.language, {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(builtAt)),
      });
    }
    // why `built` and not `builtAt`: a graph built before warhol recorded the
    // time has no builtAt, and calling it "not built yet" would be false.
    return status.data?.built
      ? t("settings.knowledgeDatabaseHeader.builtTimeUnknown")
      : t("settings.knowledgeDatabaseHeader.neverBuilt");
  }
  const lastBuilt = describeBuild();

  function handleBuild() {
    build.mutate(undefined, {
      onSuccess() {
        notify({
          title: t("settings.knowledgeDatabaseHeader.buildStartedTitle"),
          message: t("settings.knowledgeDatabaseHeader.buildStartedMessage"),
        });
      },
      async onError(error) {
        notify({
          title: t("settings.knowledgeDatabaseHeader.buildFailedTitle"),
          message: await errorMessage(error),
          variant: "error",
        });
      },
    });
  }

  return (
    <Flex align="center" gap="3" data-testid="knowledge-database-graph-build">
      <Text
        size="2"
        color="gray"
        role="status"
        aria-live="polite"
        data-testid="knowledge-database-graph-last-built"
        data-building={building ? "true" : "false"}
        data-built-at={builtAt ?? ""}
      >
        {/* why: a failed read has no builtAt either, and "not built yet"
            would then be a claim about the graph nobody checked. */}
        {!building && (status.isLoading || status.isError) ? null : lastBuilt}
      </Text>
      {canManage ? (
        <Button
          onClick={handleBuild}
          disabled={building}
          data-testid="knowledge-database-build-graph-button"
        >
          {t("settings.knowledgeDatabaseHeader.buildKnowledgeGraph")}
        </Button>
      ) : null}
    </Flex>
  );
}
